import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import PasswordProtection from "@/components/PasswordProtection";

interface DeckView {
  id: string;
  email: string;
  started_at: string;
  last_active_at: string;
  slides_viewed: number[];
  total_slides_viewed: number;
  duration_seconds: number;
  user_agent: string | null;
  created_at: string;
  deck_type: string;
}

const formatDuration = (seconds: number) => {
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}m ${secs}s`;
};

const formatDate = (dateStr: string) => {
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getDeviceType = (ua: string | null): string => {
  if (!ua) return "—";
  const lower = ua.toLowerCase();
  if (/iphone|android.*mobile|windows phone/i.test(lower)) return "📱 Mobile";
  if (/ipad|android(?!.*mobile)|tablet/i.test(lower)) return "📱 Tablet";
  return "💻 Desktop";
};

const DeckInfo = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return localStorage.getItem('deckInfoAuthenticated') === 'true';
  });
  const [views, setViews] = useState<DeckView[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchViews = async () => {
      const { data, error } = await supabase
        .from("deck_views")
        .select("*")
        .order("started_at", { ascending: false })
        .limit(200);

      if (!error && data) {
        setViews(data as DeckView[]);
      }
      setLoading(false);
    };
    fetchViews();

    // Real-time subscription
    const channel = supabase
      .channel("deck_views_changes")
      .on("postgres_changes", { event: "*", schema: "public", table: "deck_views" }, () => {
        fetchViews();
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, []);

  // Aggregate stats
  const uniqueEmails = new Set(views.map((v) => v.email)).size;
  const totalViews = views.length;
  const avgDuration = views.length > 0 ? Math.round(views.reduce((acc, v) => acc + v.duration_seconds, 0) / views.length) : 0;

  if (!isAuthenticated) {
    return (
      <PasswordProtection
        onSuccess={() => {
          setIsAuthenticated(true);
          localStorage.setItem('deckInfoAuthenticated', 'true');
        }}
        password="podsinfo2026"
      />
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-5xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="mb-10">
          <h1 className="font-funnel font-bold text-3xl text-foreground mb-1">deck analytics</h1>
          <p className="text-muted-foreground font-sans text-sm">track who's viewed the qwip pitch deck</p>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-3 gap-4 mb-10">
          {[
            { label: "total views", value: totalViews },
            { label: "unique viewers", value: uniqueEmails },
            { label: "avg. duration", value: formatDuration(avgDuration) },
          ].map((stat) => (
            <div key={stat.label} className="bg-foreground/5 border border-foreground/10 rounded-2xl p-5">
              <p className="font-funnel font-bold text-2xl text-foreground">{stat.value}</p>
              <p className="text-muted-foreground font-sans text-xs uppercase tracking-wider mt-1">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Table */}
        {loading ? (
          <p className="text-muted-foreground font-sans text-sm">loading...</p>
        ) : views.length === 0 ? (
          <p className="text-muted-foreground font-sans text-sm">no views yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                 <tr className="border-b border-foreground/10">
                   <th className="font-sans font-semibold text-xs uppercase tracking-wider text-muted-foreground pb-3 pr-4">email</th>
                    <th className="font-sans font-semibold text-xs uppercase tracking-wider text-muted-foreground pb-3 pr-4">deck</th>
                    <th className="font-sans font-semibold text-xs uppercase tracking-wider text-muted-foreground pb-3 pr-4">device</th>
                    <th className="font-sans font-semibold text-xs uppercase tracking-wider text-muted-foreground pb-3 pr-4">viewed at</th>
                    <th className="font-sans font-semibold text-xs uppercase tracking-wider text-muted-foreground pb-3 pr-4">duration</th>
                   <th className="font-sans font-semibold text-xs uppercase tracking-wider text-muted-foreground pb-3 pr-4">slides</th>
                 </tr>
              </thead>
              <tbody>
                {views.map((view) => (
                  <tr key={view.id} className="border-b border-foreground/5 hover:bg-foreground/[0.02]">
                     <td className="font-sans text-sm text-foreground py-3 pr-4">{view.email}</td>
                     <td className="font-sans text-sm text-muted-foreground py-3 pr-4">
                       <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${view.deck_type === 'investor' ? 'bg-primary/10 text-primary' : 'bg-foreground/5 text-muted-foreground'}`}>
                         {view.deck_type || 'sales'}
                       </span>
                     </td>
                    <td className="font-sans text-sm text-muted-foreground py-3 pr-4">{getDeviceType(view.user_agent)}</td>
                    <td className="font-sans text-sm text-muted-foreground py-3 pr-4">{formatDate(view.started_at)}</td>
                    <td className="font-sans text-sm text-muted-foreground py-3 pr-4">{formatDuration(view.duration_seconds)}</td>
                    <td className="font-sans text-sm text-muted-foreground py-3 pr-4">
                      {view.slides_viewed && view.slides_viewed.length > 0
                        ? `${view.total_slides_viewed} (${[...new Set(view.slides_viewed)].sort((a, b) => a - b).join(", ")})`
                        : "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeckInfo;
